import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import PDFIcon from "../../resources/pdf_with_text.png";
import loadingSvg from "../../resources/icons/loading.svg";
import Modal from "./Modal";
import { ReactComponent as UploadIcon } from "../../resources/icons/upload.svg";
import { ReactComponent as Calendar } from "../../resources/icons/calendar.svg";
import { ReactComponent as User } from "../../resources/icons/user.svg";
import { UploadedDocument } from "../../types";
import dayjs from "dayjs";

interface DocumentUploadProps {
  type: string;
  title: string;
  docs: UploadedDocument[];
  newDocs: UploadedDocument[];
  onAddNewDocs: (files: UploadedDocument[]) => void;
  onRemoveNewDoc: (name: string) => void;
  onRequestDelete: (docId: string) => void;
  maxFiles?: number;
  onPreview?: (id: string, name: string) => void;
  uploadedAt?: string;
  uploader?: {
    id: number;
    name: string;
  };
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  type,
  title,
  docs,
  newDocs,
  onAddNewDocs,
  onRemoveNewDoc,
  onRequestDelete,
  maxFiles = 20,
  onPreview,
}) => {
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState<Set<string>>(new Set());

  const handleDrop = (acceptedFiles: File[]) => {
    const totalCount = docs.length + newDocs.length;
    const spaceLeft = maxFiles - totalCount;
    if (spaceLeft <= 0) {
      setShowLimitModal(true);
      return;
    }

    const toAdd = acceptedFiles.slice(0, spaceLeft).map((file) => ({
      name: file.name,
      size: file.size,
      isNew: true,
      uploading: true,
      file,
    }));

    onAddNewDocs(toAdd);

    toAdd.forEach((doc) => {
      setUploadingDocs((prev) => new Set(prev).add(doc.name));
      setTimeout(() => {
        setUploadingDocs((prev) => {
          const next = new Set(prev);
          next.delete(doc.name);
          return next;
        });
      }, 2000);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true,
  });

  return (
    <div className="mb-6 bg-[#F9FAFB] p-4 rounded-lg">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-semibold">{title}</h3>
        <div
          {...getRootProps()}
          className="cursor-pointer  rounded px-3 py-1 text-sm text-buttonBlue transition"
        >
          <UploadIcon className="w-4 h-4 inline-block mr-1" />
          <input {...getInputProps()} />
          {isDragActive ? "Drop PDFs here..." : "Upload document"}
        </div>
      </div>

      <div className="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
        {docs.map((doc) => (
          <div
            key={doc.id}
            className={`relative bg-white border border-[#D0D5DD] rounded-lg px-4 py-2 flex items-center cursor-pointer ${
              !doc.canDelete ? "opacity-50" : ""
            }`}
            onClick={() =>
              onPreview && doc.id && doc.name && onPreview(doc.id, doc.name)
            }
          >
            <div className="relative w-[42px] mr-2 flex-shrink-0">
              <img src={PDFIcon} alt="PDF" className="w-full h-full" />
            </div>
            <div className="flex-1 flex flex-col overflow-hidden">
              <p className="text-sm font-medium truncate max-w-[80%]">
                {decodeURIComponent(doc.name)}
              </p>
              <p className="text-xs text-gray-500">
                {(doc.size / 1024).toFixed(1)} KB
              </p>

              <div className="mt-1 flex items-center space-x-3 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{dayjs(doc.uploadedAt).format("DD.MM.YYYY")}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>{doc.uploader?.name}</span>
                </div>
              </div>
            </div>
            {doc.canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestDelete(doc.id!);
                }}
                className="absolute top-2 right-2 text-[#667085] text-sm p-1"
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {newDocs.map((doc) => (
          <div
            key={doc.name}
            className="relative bg-white border border-[#D0D5DD] rounded-lg px-4 py-2 flex items-center"
          >
            <div className="relative w-[42px] mr-2 flex-shrink-0">
              <img src={PDFIcon} alt="PDF" className="w-full h-full" />
            </div>
            <div className="flex-1 flex flex-col overflow-hidden">
              <p className="text-sm font-medium truncate max-w-[80%]">
                {decodeURIComponent(doc.name)}
              </p>
              <p className="text-xs text-gray-500">
                {(doc.size / 1024).toFixed(1)} KB
              </p>
            </div>

            {uploadingDocs.has(doc.name) ? (
              <img
                src={loadingSvg}
                className="w-5 h-5 animate-spin absolute top-2 right-2"
                style={{ animationDuration: "1.2s" }}
                alt="Uploading"
              />
            ) : (
              <button
                onClick={() => onRemoveNewDoc(doc.name)}
                className="absolute top-2 right-2 text-[#667085] text-sm p-1"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      <Modal isOpen={showLimitModal}>
        <div className="p-6">
          <h2 className="text-lg font-bold mb-2">Document Limit Reached</h2>
          <p className="text-sm text-gray-600 mb-4">
            You have reached the maximum of {maxFiles} documents for{" "}
            <strong>{title}</strong>.
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => setShowLimitModal(false)}
              className="px-4 py-1 bg-blue-600 text-white rounded"
            >
              OK
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DocumentUpload;
